import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Clock, Calculator, CheckCircle2, Send, Plus, X, Loader2,
  Play, DollarSign, RefreshCw, Eye, AlertTriangle, Edit, Trash2,
  ChevronLeft, ChevronRight, Calendar, Save, Upload, FileText, 
  Camera, Sparkles, ShieldCheck, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The server answers with plain text, or with { message, errors: [...] } (e.g. a bad or duplicate SSN)
const describeError = (detail, fallback) => {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) return [detail.message, ...(detail.errors || [])].filter(Boolean).join(': ') || fallback;
  return fallback;
};

const PayrollPage = () => {
  // The company on screen (the context has no `currentCompany`, so this page used to fall back to one fixed company
  // and file every employee, time entry and payroll run there whichever company was open)
  const { companyId, selectedCompany } = useCompany();
  const { user } = useAuth();
  const canUnpost = ['admin', 'manager', 'accountant'].includes(user?.role);
  
  // Current step in payroll workflow
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  
  // Employees state
  const [employees, setEmployees] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    first_name: '', last_name: '', ssn: '', 
    pay_rate: '', pay_type: 'hourly', employment_type: 'full-time',
    filing_status: 'single', allowances: 0, additional_withholding: 0,
    address: '', city: '', state: 'CA', zip: ''
  });
  
  // Time entries state
  const [timeEntries, setTimeEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    employee_id: '', date: new Date().toISOString().split('T')[0], 
    regular_hours: '', overtime_hours: ''
  });
  
  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('csv'); // 'csv' or 'ocr'
  const [csvData, setCsvData] = useState('');
  const [ocrResult, setOcrResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const ocrFileInputRef = useRef(null);
  
  // AI Quality Check state
  const [qualityCheckResult, setQualityCheckResult] = useState(null);
  const [showQualityModal, setShowQualityModal] = useState(false);
  
  // Payroll calculation state
  const [payPeriod, setPayPeriod] = useState({ start: '', end: '', pay_date: '' });
  const [paychecks, setPaychecks] = useState([]);
  const [payRegister, setPayRegister] = useState(null);
  
  // Payroll runs state
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [currentRun, setCurrentRun] = useState(null);


  const steps = [
    { num: 1, label: 'Employees', icon: Users },
    { num: 2, label: 'Time Entry', icon: Clock },
    { num: 3, label: 'Calculate', icon: Calculator },
    { num: 4, label: 'Review', icon: Eye },
    { num: 5, label: 'Post', icon: Send }
  ];

  // Fetch data on mount
  useEffect(() => {
    fetchEmployees();
    fetchTimeEntries();
    fetchPayrollRuns();
  }, [companyId]);

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/employees?company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entries?company_id=${companyId}`);
      if (res.ok) {
        const data = await res.json();
        setTimeEntries(data);
      }
    } catch (err) {
      console.error('Failed to fetch time entries:', err);
    }
  };

  const fetchPayrollRuns = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll-runs?company_id=${companyId}`);
      if (res.ok) {
        const runs = await res.json();
        setPayrollRuns(runs);
        const unposted = runs.find(r => r.status === 'calculated');
        if (unposted) setCurrentRun(unposted);
      }
    } catch (err) {
      console.error('Failed to fetch payroll runs:', err);
    }
  };

  // Calculate totals
  const totalRegularHours = timeEntries.reduce((sum, e) => sum + (parseFloat(e.regular_hours) || 0), 0);
  const totalOvertimeHours = timeEntries.reduce((sum, e) => sum + (parseFloat(e.overtime_hours) || 0), 0);
  const totalHours = totalRegularHours + totalOvertimeHours;

  const handleAddEmployee = async () => {
    if (!newEmployee.first_name || !newEmployee.last_name || !newEmployee.pay_rate) {
      toast.error('First name, last name, and pay rate are required');
      return;
    }
    setLoading(true);
    try {
      const employeeData = {
        ...newEmployee,
        name: `${newEmployee.first_name} ${newEmployee.last_name}`.trim(),
        company_id: companyId,
        hourly_rate: newEmployee.pay_type === 'hourly' ? newEmployee.pay_rate : (parseFloat(newEmployee.pay_rate) / 2080).toFixed(2)
      };
      
      const res = await fetch(`${API_URL}/api/payroll-full/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employeeData)
      });
      if (res.ok) {
        toast.success('Employee added successfully');
        setNewEmployee({
          first_name: '', last_name: '', ssn: '', pay_rate: '', pay_type: 'hourly',
          employment_type: 'full-time', filing_status: 'single', allowances: 0,
          additional_withholding: 0, address: '', city: '', state: 'CA', zip: ''
        });
        setShowAddEmployee(false);
        fetchEmployees();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(describeError(err.detail, 'Failed to add employee'));
      }
    } catch (err) {
      toast.error('Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeEntry = async () => {
    if (!newEntry.employee_id || (!newEntry.regular_hours && !newEntry.overtime_hours)) {
      toast.error('Employee and hours are required');
      return;
    }
    setLoading(true);
    try {
      const entryData = {
        employee_id: newEntry.employee_id,
        date: newEntry.date,
        regular_hours: parseFloat(newEntry.regular_hours) || 0,
        overtime_hours: parseFloat(newEntry.overtime_hours) || 0,
        company_id: companyId
      };
      
      const res = await fetch(`${API_URL}/api/ca-payroll/time-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entryData)
      });
      if (res.ok) {
        toast.success('Time entry added');
        setNewEntry({ ...newEntry, employee_id: '', regular_hours: '', overtime_hours: '' });
        fetchTimeEntries();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add time entry');
      }
    } catch (err) {
      toast.error('Failed to add time entry');
    } finally {
      setLoading(false);
    }
  };

  // CSV Import Handler
  const handleCSVImport = async () => {
    if (!csvData.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    setImportLoading(true);
    try {
      // Parse CSV
      const lines = csvData.trim().split('\n');
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      const data = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const entry = {};
        headers.forEach((h, idx) => {
          if (h.includes('employee') && h.includes('id')) entry.employee_id = values[idx];
          else if (h.includes('employee') || h.includes('name')) entry.employee_id = values[idx]; // Will match by name
          else if (h.includes('date')) entry.date = values[idx];
          else if (h.includes('regular') || h === 'hours') entry.regular_hours = parseFloat(values[idx]) || 0;
          else if (h.includes('overtime') || h.includes('ot')) entry.overtime_hours = parseFloat(values[idx]) || 0;
          else if (h.includes('double') || h.includes('dt')) entry.double_time_hours = parseFloat(values[idx]) || 0;
          else if (h.includes('job') || h.includes('code')) entry.job_code = values[idx];
          else if (h.includes('note')) entry.notes = values[idx];
        });
        if (entry.employee_id || entry.date) data.push(entry);
      }
      
      const res = await fetch(`${API_URL}/api/reports/import-timesheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, company_id: companyId })
      });
      
      const result = await res.json();
      if (result.success) {
        toast.success(`Imported ${result.imported} time entries`);
        if (result.errors?.length > 0) {
          toast.warning(`${result.errors.length} entries had errors`);
        }
        setShowImportModal(false);
        setCsvData('');
        fetchTimeEntries();
      } else {
        toast.error(result.message || 'Import failed');
      }
    } catch (err) {
      toast.error('Failed to import CSV');
    } finally {
      setImportLoading(false);
    }
  };

  // OCR Upload Handler
  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    setOcrResult(null);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const mimeType = file.type || 'image/jpeg';
        
        const res = await fetch(`${API_URL}/api/reports/timesheet-ocr`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_base64: base64, mime_type: mimeType, company_id: companyId })
        });
        
        const result = await res.json();
        if (result.success) {
          setOcrResult(result);
          toast.success(`Extracted ${result.entries?.length || 0} time entries. Review before saving.`);
        } else {
          toast.error(result.error || 'OCR processing failed');
        }
        setImportLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to process image');
      setImportLoading(false);
    }
  };

  // Save OCR Results
  const handleSaveOCREntries = async () => {
    if (!ocrResult?.entries?.length) {
      toast.error('No entries to save');
      return;
    }
    setImportLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/timesheet-ocr-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: ocrResult.entries, company_id: companyId })
      });
      
      const result = await res.json();
      if (result.success) {
        toast.success(`Saved ${result.saved} time entries`);
        setShowImportModal(false);
        setOcrResult(null);
        fetchTimeEntries();
      } else {
        toast.error(result.message || 'Failed to save entries');
      }
    } catch (err) {
      toast.error('Failed to save OCR entries');
    } finally {
      setImportLoading(false);
    }
  };

  // AI Quality Check Handler
  const handleAIQualityCheck = async () => {
    if (!payPeriod.start || !payPeriod.end) {
      toast.error('Please select pay period dates first');
      return;
    }
    setAiProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/ai-quality-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          pay_period_start: payPeriod.start,
          pay_period_end: payPeriod.end
        })
      });
      
      const result = await res.json();
      setQualityCheckResult(result);
      setShowQualityModal(true);
      
      if (result.ready_for_payroll) {
        toast.success('Quality check passed! Ready for payroll.');
      } else {
        toast.warning(`Found ${result.summary?.issues_found} issues and ${result.summary?.warnings_found} warnings`);
      }
    } catch (err) {
      toast.error('AI quality check failed');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleRunPayroll = async () => {
    if (!payPeriod.start || !payPeriod.end) {
      toast.error('Please select pay period dates');
      return;
    }
    
    setAiProcessing(true);
    toast.info('Running payroll calculation...');
    
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          pay_period_start: payPeriod.start,
          pay_period_end: payPeriod.end,
          pay_date: payPeriod.pay_date || payPeriod.end
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('Payroll result:', data);
        setPaychecks(data.calculations || []);
        setPayRegister(data);
        setCurrentRun(data);
        setCurrentStep(4);
        toast.success(`Payroll calculated for ${data.calculations?.length || 0} employees`);
        fetchPayrollRuns();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to run payroll');
      }
    } catch (err) {
      console.error('Payroll error:', err);
      toast.error('Error running payroll');
    } finally {
      setAiProcessing(false);
    }
  };

  const handlePostPayroll = async () => {
    if (!currentRun?.payroll_id) {
      toast.error('No payroll to post');
      return;
    }
    
    if (!window.confirm('Post this payroll? This updates year-to-date records and the GL. An administrator, manager or accountant can unpost it later if it needs correcting.')) {
      return;
    }
    
    setAiProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll/${currentRun.payroll_id}/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId })
      });
      
      if (res.ok) {
        const posted = await res.json().catch(() => ({}));
        if (posted.status === 'blocked') {
          toast.error(posted.checklist?.find((c) => c.status === 'error')?.message || posted.message || 'Payroll could not be posted');
        } else {
          toast.success('Payroll posted successfully!');
          setCurrentRun({ ...currentRun, status: 'posted' });
          fetchPayrollRuns();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to post payroll');
      }
    } catch (err) {
      toast.error('Error posting payroll');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleUnpostPayroll = async () => {
    const reason = window.prompt('Why is this payroll being unposted? (it is saved in the audit trail)');
    if (!reason) return;
    setAiProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/payroll/${currentRun.payroll_id}/unpost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Payroll unposted. You can correct it and post it again.');
        setCurrentRun({ ...currentRun, status: 'calculated' });
        fetchPayrollRuns();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Could not unpost the payroll');
      }
    } catch (err) {
      toast.error('Could not unpost the payroll');
    } finally {
      setAiProcessing(false);
    }
  };

  // Quick date selectors
  const setQuickPeriod = (type) => {
    const today = new Date();
    let start, end;
    
    if (type === 'thisWeek') {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (type === 'lastWeek') {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day - 7);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else if (type === 'lastTwoWeeks') {
      const day = today.getDay();
      start = new Date(today);
      start.setDate(today.getDate() - day - 14);
      end = new Date(today);
      end.setDate(today.getDate() - day - 1);
    }
    
    setPayPeriod({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      pay_date: end.toISOString().split('T')[0]
    });
  };

  // Employee View/Edit Modal
  const EmployeeModal = ({ employee, onClose }) => {
    const [editData, setEditData] = useState({ ...employee });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      setSaving(true);
      try {
        const res = await fetch(`${API_URL}/api/payroll-full/employees/${employee.employee_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...editData,
            // keep the name shown in lists in step with the first and last name that were edited
            name: [editData.first_name, editData.last_name].filter(Boolean).join(' ') || editData.name,
            company_id: companyId
          })
        });
        if (res.ok) {
          toast.success('Employee updated');
          fetchEmployees();
          onClose();
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(describeError(err.detail, 'Failed to update'));
        }
      } catch (err) {
        toast.error('Error updating employee');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-bold">Employee Details</h3>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">First Name</label>
                <Input value={editData.first_name || editData.name?.split(' ')[0] || ''} 
                  onChange={e => setEditData({...editData, first_name: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Last Name</label>
                <Input value={editData.last_name || editData.name?.split(' ').slice(1).join(' ') || ''} 
                  onChange={e => setEditData({...editData, last_name: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500">SSN</label>
                <Input value={editData.ssn || ''} 
                  onChange={e => setEditData({...editData, ssn: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Pay Rate</label>
                <Input type="number" value={editData.pay_rate || editData.hourly_rate || ''} 
                  onChange={e => setEditData({...editData, pay_rate: e.target.value, hourly_rate: e.target.value})} />
              </div>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="text-sm font-medium text-amber-800 mb-3">Tax Withholding (W-4)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-500">Filing Status</label>
                  <select className="w-full h-10 border rounded-md px-2"
                    value={editData.filing_status || 'single'}
                    onChange={e => setEditData({...editData, filing_status: e.target.value})}>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Allowances</label>
                  <Input type="number" value={editData.allowances || 0} 
                    onChange={e => setEditData({...editData, allowances: parseInt(e.target.value) || 0})} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Additional Withholding</label>
                  <Input type="number" value={editData.additional_withholding || 0} 
                    onChange={e => setEditData({...editData, additional_withholding: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border-t flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Employee Edit Modal */}
      {editingEmployee && (
        <EmployeeModal employee={editingEmployee} onClose={() => setEditingEmployee(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll</h1>
          <p className="text-slate-500">Process employee payroll</p>
        </div>
        <div className="flex items-center gap-4">
          <a href="/tax-setup" className="text-sm text-sky-600 hover:underline">Tax Setup →</a>
          <span className="font-medium text-sky-700">{selectedCompany?.name || 'Blue Skies Farm'}</span>
        </div>
      </div>

      {/* Step Progress */}
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border">
        {steps.map((step, idx) => (
          <React.Fragment key={step.num}>
            <button
              onClick={() => setCurrentStep(step.num)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                currentStep === step.num ? 'bg-sky-100 text-sky-700' : 
                currentStep > step.num ? 'text-emerald-600' : 'text-slate-400'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep === step.num ? 'bg-sky-600 text-white' : 
                currentStep > step.num ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100'
              }`}>
                {currentStep > step.num ? <CheckCircle2 className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              <span className="text-xs font-medium">{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.num ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Employees */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-sky-600" />
                  Employees ({employees.length})
                </CardTitle>
                <CardDescription>Manage payroll employees and tax settings</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="employee-search-input"
                    placeholder="Search by name, ID, or SSN..."
                    className="pl-8 w-64"
                    onChange={async (e) => {
                      const val = e.target.value.trim();
                      if (!val) { fetchEmployees(); return; }
                      try {
                        const isSSN = /^\d{3}-?\d{2}-?\d{4}$/.test(val.replace(/\s/g, ''));
                        const param = isSSN ? `ssn=${encodeURIComponent(val)}` : `search=${encodeURIComponent(val)}`;
                        const res = await fetch(`${API_URL}/api/ca-payroll/employees?company_id=${companyId}&${param}`);
                        if (res.ok) setEmployees(await res.json());
                      } catch (err) { console.error('Search failed:', err); }
                    }}
                  />
                </div>
                <Button onClick={() => setShowAddEmployee(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Employee
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {showAddEmployee && (
              <div className="mb-6 p-4 bg-sky-50 rounded-lg border border-sky-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Add New Employee</h4>
                  <Button variant="ghost" size="sm" onClick={() => setShowAddEmployee(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <Input placeholder="First Name *" value={newEmployee.first_name}
                    onChange={e => setNewEmployee({...newEmployee, first_name: e.target.value})} />
                  <Input placeholder="Last Name *" value={newEmployee.last_name}
                    onChange={e => setNewEmployee({...newEmployee, last_name: e.target.value})} />
                  <Input placeholder="SSN" value={newEmployee.ssn}
                    onChange={e => setNewEmployee({...newEmployee, ssn: e.target.value})} />
                  <Input type="number" placeholder="Hourly Rate *" value={newEmployee.pay_rate}
                    onChange={e => setNewEmployee({...newEmployee, pay_rate: e.target.value})} />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <select className="border rounded-md p-2" value={newEmployee.filing_status}
                    onChange={e => setNewEmployee({...newEmployee, filing_status: e.target.value})}>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                  <Input type="number" placeholder="Allowances" value={newEmployee.allowances}
                    onChange={e => setNewEmployee({...newEmployee, allowances: parseInt(e.target.value) || 0})} />
                  <select className="border rounded-md p-2" value={newEmployee.employment_type}
                    onChange={e => setNewEmployee({...newEmployee, employment_type: e.target.value})}>
                    <option value="full-time">Full-Time</option>
                    <option value="part-time">Part-Time</option>
                  </select>
                  <Button onClick={handleAddEmployee} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add
                  </Button>
                </div>
              </div>
            )}

            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">SSN</th>
                  <th className="text-right p-3">Pay Rate</th>
                  <th className="text-center p-3">Filing Status</th>
                  <th className="text-center p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.employee_id} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.employee_id}</p>
                    </td>
                    <td className="p-3 font-mono text-sm text-slate-500">
                      {emp.ssn ? `***-**-${emp.ssn.slice(-4)}` : 'Not set'}
                    </td>
                    <td className="p-3 text-right font-medium text-emerald-600">
                      ${parseFloat(emp.pay_rate || emp.hourly_rate || 0).toFixed(2)}/hr
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        emp.filing_status === 'married' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100'
                      }`}>
                        {emp.filing_status === 'married' ? 'Married' : 'Single'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        data-testid={`edit-employee-${emp.employee_id}`}
                        onClick={() => setEditingEmployee(emp)}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end mt-4">
              <Button onClick={() => setCurrentStep(2)}>
                Continue to Time Entry <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Time Entry */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-sky-600" />
                  Time Entries ({timeEntries.length})
                </CardTitle>
                <CardDescription>Record employee hours worked</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowImportModal(true)}>
                  <Upload className="w-4 h-4 mr-2" /> Import
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium mb-3">Add Time Entry</h4>
              <div className="grid grid-cols-5 gap-3">
                <select className="border rounded-md p-2" value={newEntry.employee_id}
                  onChange={e => setNewEntry({...newEntry, employee_id: e.target.value})}>
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                  ))}
                </select>
                <Input type="date" value={newEntry.date}
                  onChange={e => setNewEntry({...newEntry, date: e.target.value})} />
                <Input type="number" placeholder="Regular Hours" value={newEntry.regular_hours}
                  onChange={e => setNewEntry({...newEntry, regular_hours: e.target.value})} />
                <Input type="number" placeholder="OT Hours" value={newEntry.overtime_hours}
                  onChange={e => setNewEntry({...newEntry, overtime_hours: e.target.value})} />
                <Button onClick={handleAddTimeEntry} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-sky-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-sky-700">{employees.length}</p>
                <p className="text-xs text-slate-500">Employees</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-700">{timeEntries.length}</p>
                <p className="text-xs text-slate-500">Time Entries</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-700">{totalHours.toFixed(1)}h</p>
                <p className="text-xs text-slate-500">Total Hours</p>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">Employee</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-right p-2">Regular</th>
                  <th className="text-right p-2">OT</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {timeEntries.slice(0, 15).map((entry, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">{entry.employee_name || employees.find(e => e.employee_id === entry.employee_id)?.name || entry.employee_id}</td>
                    <td className="p-2">{entry.date}</td>
                    <td className="p-2 text-right">{entry.regular_hours || 0}h</td>
                    <td className="p-2 text-right text-amber-600">{entry.overtime_hours || 0}h</td>
                    <td className="p-2 text-right font-medium">
                      {((parseFloat(entry.regular_hours) || 0) + (parseFloat(entry.overtime_hours) || 0)).toFixed(1)}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-between mt-4">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setCurrentStep(3)}>
                Continue to Calculate <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Calculate */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-sky-600" />
              Run Payroll Calculation
            </CardTitle>
            <CardDescription>Select pay period and calculate payroll</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium mb-3">Pay Period</h4>
              <div className="flex gap-2 mb-3">
                <Button variant="outline" size="sm" onClick={() => setQuickPeriod('lastWeek')}>Last Week</Button>
                <Button variant="outline" size="sm" onClick={() => setQuickPeriod('thisWeek')}>This Week</Button>
                <Button variant="outline" size="sm" onClick={() => setQuickPeriod('lastTwoWeeks')}>Last 2 Weeks</Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Start Date *</label>
                  <Input type="date" value={payPeriod.start}
                    onChange={e => setPayPeriod({...payPeriod, start: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm text-slate-500">End Date *</label>
                  <Input type="date" value={payPeriod.end}
                    onChange={e => setPayPeriod({...payPeriod, end: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm text-slate-500">Pay Date</label>
                  <Input type="date" value={payPeriod.pay_date}
                    onChange={e => setPayPeriod({...payPeriod, pay_date: e.target.value})} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-sky-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-sky-700">{employees.length}</p>
                <p className="text-sm text-slate-500">Employees</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-emerald-700">{timeEntries.length}</p>
                <p className="text-sm text-slate-500">Time Entries</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-amber-700">{totalHours.toFixed(0)}h</p>
                <p className="text-sm text-slate-500">Total Hours</p>
              </div>
            </div>

            {!payPeriod.start || !payPeriod.end ? (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Please select start and end dates for the pay period
                </p>
              </div>
            ) : null}

            {/* AI Quality Check */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-sky-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-purple-900">AI Input Quality Agent</h4>
                    <p className="text-sm text-purple-600">Validates entries for missing hours, duplicates, and anomalies</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleAIQualityCheck}
                  disabled={aiProcessing || !payPeriod.start || !payPeriod.end}
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  {aiProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Run Quality Check
                </Button>
              </div>
              {qualityCheckResult && (
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <div className="flex items-center gap-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      qualityCheckResult.ready_for_payroll 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      Score: {qualityCheckResult.summary?.quality_score || 0}/100
                    </div>
                    <span className="text-sm text-slate-600">
                      {qualityCheckResult.summary?.issues_found || 0} issues, {qualityCheckResult.summary?.warnings_found || 0} warnings
                    </span>
                    <Button variant="link" size="sm" onClick={() => setShowQualityModal(true)} className="text-purple-600">
                      View Details
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={handleRunPayroll} disabled={aiProcessing || !payPeriod.start || !payPeriod.end}
                className="bg-emerald-600 hover:bg-emerald-700">
                {aiProcessing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Run Payroll Calculation</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {currentStep === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-sky-600" />
              Review Payroll
            </CardTitle>
            <CardDescription>Review calculated paychecks before posting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-5 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-xl font-bold">{paychecks.length}</p>
                <p className="text-xs text-slate-500">Employees</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-xl font-bold text-emerald-600">
                  ${(payRegister?.totals?.total_gross || payRegister?.summary?.total_gross || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Gross Pay</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-xl font-bold text-red-600">
                  ${(payRegister?.totals?.total_employee_taxes || payRegister?.summary?.total_deductions || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Deductions</p>
              </div>
              <div className="p-3 bg-sky-50 rounded-lg text-center">
                <p className="text-xl font-bold text-sky-600">
                  ${paychecks.reduce((sum, p) => sum + (p.gross_pay?.flsa_ot_premium || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">FLSA OT Deduction</p>
              </div>
              <div className="p-3 bg-sky-50 rounded-lg text-center">
                <p className="text-xl font-bold text-sky-600">
                  ${(payRegister?.totals?.total_net || payRegister?.summary?.total_net || 0).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Net Pay</p>
              </div>
            </div>

            {paychecks.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3">Employee</th>
                    <th className="text-right p-3">Gross Pay</th>
                    <th className="text-right p-3">Fed Tax</th>
                    <th className="text-right p-3">State Tax</th>
                    <th className="text-right p-3">FICA</th>
                    <th className="text-right p-3">FLSA OT Ded</th>
                    <th className="text-right p-3">Net Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {paychecks.map((p, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-3 font-medium">{p.employee_name}</td>
                      <td className="p-3 text-right">${(p.gross_pay?.total_gross || p.gross_pay || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-red-600">-${(p.deductions?.federal_withholding || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-red-600">-${(p.deductions?.ca_state_withholding || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-red-600">
                        -${((p.deductions?.social_security || 0) + (p.deductions?.medicare || 0)).toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-sky-600">
                        {(p.gross_pay?.flsa_ot_premium || 0) > 0 ? `$${p.gross_pay.flsa_ot_premium.toLocaleString()}` : '-'}
                      </td>
                      <td className="p-3 text-right font-bold text-emerald-600">${(p.net_pay || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No payroll calculations yet. Go back and run payroll.
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setCurrentStep(5)} className="bg-sky-600 hover:bg-sky-700" disabled={paychecks.length === 0}>
                Approve & Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Post */}
      {currentStep === 5 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-sky-600" />
              Post Payroll
            </CardTitle>
            <CardDescription>Finalize and post payroll to GL</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentRun?.status === 'posted' ? (
              <div className="text-center py-12 bg-emerald-50 rounded-lg">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-emerald-800">Payroll Posted!</h3>
                <p className="text-emerald-600 mt-2">YTD records and GL have been updated.</p>
                <div className="mt-4 flex justify-center gap-3">
                  <Button variant="outline" onClick={() => { setCurrentStep(1); setPaychecks([]); setCurrentRun(null); }}>
                    Start New Payroll
                  </Button>
                  {canUnpost && (
                    <Button variant="outline" onClick={handleUnpostPayroll} disabled={aiProcessing} data-testid="unpost-payroll-btn">
                      Unpost payroll
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Final Step: Post Payroll
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {paychecks.length} employees calculated
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Gross: ${(payRegister?.summary?.total_gross ?? payRegister?.totals?.total_gross ?? 0).toLocaleString()}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Net: ${(payRegister?.summary?.total_net ?? payRegister?.totals?.total_net ?? 0).toLocaleString()}
                    </li>
                  </ul>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(4)}>
                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  <Button onClick={handlePostPayroll} disabled={aiProcessing || !currentRun?.payroll_id}
                    className="bg-emerald-600 hover:bg-emerald-700">
                    {aiProcessing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Post Payroll to GL</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Import Time Entries</h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowImportModal(false); setOcrResult(null); setCsvData(''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  variant={importMode === 'csv' ? 'default' : 'outline'} 
                  size="sm" 
                  onClick={() => setImportMode('csv')}
                >
                  <FileText className="w-4 h-4 mr-2" /> CSV Import
                </Button>
                <Button 
                  variant={importMode === 'ocr' ? 'default' : 'outline'} 
                  size="sm" 
                  onClick={() => setImportMode('ocr')}
                >
                  <Camera className="w-4 h-4 mr-2" /> OCR Scan
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {importMode === 'csv' ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">
                      Paste CSV data with columns: employee_id, date, regular_hours, overtime_hours
                    </p>
                    <textarea
                      className="w-full h-48 border rounded-lg p-3 font-mono text-sm"
                      placeholder="employee_id,date,regular_hours,overtime_hours&#10;EMP-001,2026-02-17,8,2&#10;EMP-002,2026-02-17,8,0"
                      value={csvData}
                      onChange={e => setCsvData(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
                    <Button onClick={handleCSVImport} disabled={importLoading || !csvData.trim()}>
                      {importLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Import CSV
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">Upload a timesheet, time card, or attendance record image</p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      ref={ocrFileInputRef}
                      onChange={handleOCRUpload}
                      className="hidden"
                    />
                    <Button onClick={() => ocrFileInputRef.current?.click()} disabled={importLoading}>
                      {importLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Select File
                    </Button>
                  </div>
                  
                  {ocrResult && (
                    <div className="space-y-4">
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-emerald-700 font-medium">
                          <CheckCircle2 className="w-4 h-4 inline mr-2" />
                          Extracted {ocrResult.entries?.length || 0} entries (Confidence: {((ocrResult.confidence || 0) * 100).toFixed(0)}%)
                        </p>
                      </div>
                      
                      {ocrResult.warnings?.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-amber-700 font-medium mb-2">Warnings:</p>
                          <ul className="text-sm text-amber-600 space-y-1">
                            {ocrResult.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                          </ul>
                        </div>
                      )}
                      
                      <table className="w-full text-sm border rounded-lg overflow-hidden">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="p-2 text-left">Employee</th>
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-right">Reg</th>
                            <th className="p-2 text-right">OT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ocrResult.entries?.map((entry, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">
                                {entry.employee_name || entry.employee_id}
                                {entry.matched_employee && (
                                  <span className="text-xs text-emerald-600 ml-1">✓ Matched</span>
                                )}
                              </td>
                              <td className="p-2">{entry.date}</td>
                              <td className="p-2 text-right">{entry.regular_hours}h</td>
                              <td className="p-2 text-right">{entry.overtime_hours}h</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => { setOcrResult(null); }}>Clear</Button>
                        <Button onClick={handleSaveOCREntries} disabled={importLoading}>
                          {importLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Entries
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Quality Check Modal */}
      {showQualityModal && qualityCheckResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Quality Check Results
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowQualityModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{qualityCheckResult.summary?.employees_checked || 0}</p>
                  <p className="text-xs text-slate-500">Employees</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold">{qualityCheckResult.summary?.total_entries || 0}</p>
                  <p className="text-xs text-slate-500">Entries</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{qualityCheckResult.summary?.issues_found || 0}</p>
                  <p className="text-xs text-slate-500">Issues</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{qualityCheckResult.summary?.warnings_found || 0}</p>
                  <p className="text-xs text-slate-500">Warnings</p>
                </div>
              </div>
              
              {/* Quality Score */}
              <div className={`p-4 rounded-lg border ${
                qualityCheckResult.ready_for_payroll ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Quality Score</span>
                  <span className={`text-2xl font-bold ${
                    qualityCheckResult.summary?.quality_score >= 80 ? 'text-emerald-600' : 
                    qualityCheckResult.summary?.quality_score >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {qualityCheckResult.summary?.quality_score || 0}/100
                  </span>
                </div>
                <p className={`text-sm mt-2 ${qualityCheckResult.ready_for_payroll ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {qualityCheckResult.ready_for_payroll ? 
                    '✓ Ready for payroll processing' : 
                    '⚠ Review issues before processing payroll'}
                </p>
              </div>
              
              {/* Issues */}
              {qualityCheckResult.issues?.length > 0 && (
                <div>
                  <h3 className="font-medium text-red-700 mb-2">Issues (Must Fix)</h3>
                  <div className="space-y-2">
                    {qualityCheckResult.issues.map((issue, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-red-800">{issue.employee_name}</p>
                            <p className="text-sm text-red-600">{issue.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warnings */}
              {qualityCheckResult.warnings?.length > 0 && (
                <div>
                  <h3 className="font-medium text-amber-700 mb-2">Warnings (Review)</h3>
                  <div className="space-y-2">
                    {qualityCheckResult.warnings.slice(0, 5).map((warning, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">{warning.employee_name}</p>
                            <p className="text-sm text-amber-600">{warning.message}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {qualityCheckResult.warnings.length > 5 && (
                      <p className="text-sm text-slate-500 text-center">
                        +{qualityCheckResult.warnings.length - 5} more warnings
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* No Issues */}
              {qualityCheckResult.issues?.length === 0 && qualityCheckResult.warnings?.length === 0 && (
                <div className="text-center py-8 bg-emerald-50 rounded-lg">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-emerald-700 font-medium">All checks passed!</p>
                  <p className="text-sm text-emerald-600">Your time entries are ready for payroll processing.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t flex justify-end">
              <Button onClick={() => setShowQualityModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
